import { PaymentPlan } from '../models/payment-plan';
import { PaymentPlanTier } from '../models/payment';
import { BillingTabDescriptor } from './interfaces';

export enum BillingRoute {
  INITIAL_SETUP = 'initial-setup',
  PLAN_DETAILS = 'plan-details',
  PAYMENT_HISTORY = 'payment-history',
  BILLING_DETAILS = 'billing-details',
  EDIT_BILLING_DETAILS = 'edit-billing-details',
  CARDS = 'cards',
  USAGE_SUMMARY = 'usage-summary'
}

interface BuildBillingTabsProps {
  initialNav: boolean;
  enteredCardDetails: boolean;
  currentPlan?: PaymentPlan;
  isWorkspaceTeam: boolean;
  canViewBilling: boolean;
  canEditBilling: boolean;
  /** The plan fetch failed. Nothing derived from it can be claimed, but the rest still stands. */
  planUnavailable: boolean;
}

/**
 * The Billing rows, decided from the plan alone. Both the settings sidebar and the Billing
 * page's own router read this one list, so they cannot disagree about which rows exist.
 *
 * Usage Summary is deliberately the only row with no condition on it: it is plan-independent and
 * ungated, so a failed plan fetch narrows the section down to it rather than emptying it.
 */
export function buildBillingTabDescriptors({
  initialNav,
  enteredCardDetails,
  currentPlan,
  isWorkspaceTeam,
  canViewBilling,
  canEditBilling,
  planUnavailable
}: BuildBillingTabsProps): BillingTabDescriptor[] {
  const hasBillingHistory = enteredCardDetails || currentPlan?.tier !== PaymentPlanTier.FREE;
  // Every row below Usage Summary is a statement about the plan.
  const hidePlanRows = planUnavailable || isWorkspaceTeam || !canViewBilling;

  return [
    {
      label: 'Plan Selection',
      path: '/payment/initial-setup',
      route: BillingRoute.INITIAL_SETUP,
      icon: 'plan',
      hidden: hidePlanRows || !initialNav
    },
    {
      label: 'Plan Details',
      path: '/payment/plan-details',
      route: BillingRoute.PLAN_DETAILS,
      icon: 'plan',
      hidden: hidePlanRows || initialNav
    },
    {
      label: 'Billing History',
      path: '/payment/payment-history',
      route: BillingRoute.PAYMENT_HISTORY,
      icon: 'payment-history',
      hidden: hidePlanRows || initialNav || !hasBillingHistory
    },
    {
      label: 'Billing Details',
      path: '/payment/billing-details',
      route: BillingRoute.BILLING_DETAILS,
      icon: 'billing-details',
      // Edit Billing Details has no row of its own — it opens from the "Edit Info" button on
      // this page, so this row stays selected there.
      matchPaths: ['/payment/edit-billing-details'],
      hidden: hidePlanRows || initialNav || !hasBillingHistory
    },
    {
      label: 'Edit Billing Details',
      path: '/payment/edit-billing-details',
      route: BillingRoute.EDIT_BILLING_DETAILS,
      hidden: true
    },
    {
      label: 'Payment Methods',
      path: '/payment/cards',
      route: BillingRoute.CARDS,
      icon: 'cards-payment',
      hidden: hidePlanRows || initialNav || !hasBillingHistory || !canEditBilling
    },
    {
      label: 'Usage Summary',
      path: '/payment/usage-summary',
      route: BillingRoute.USAGE_SUMMARY,
      icon: 'graph'
    }
  ];
}
